import type { OrderDetailPerspectiveView, OrderDetailResponse } from './queries'

/**
 * Mock order data for development/testing.
 * Navigate to /operations/orders/MOCK-001 to view this order.
 */
const MOCK_ORDER: OrderDetailPerspectiveView = {
  id: 'mock_ord_001',
  internal_order_number: 'MOCK-001',
  partner_order_number: 'PTN-78542',
  po: 'PO-00892',
  status: 'synced',
  order_status: null,
  trip_status: 'driving_to_drop',
  eta: '2024-01-17T14:00:00Z',
  integration: 'partner_system',
  delivery_window_start: '2024-01-17T08:00:00Z',
  delivery_window_end: '2024-01-17T16:00:00Z',
  product_ratios: [],
  suppliers: ['Acme Fuels', 'Gulf Coast Energy'],
  origins: ['Houston Terminal'],
  destinations: ['Quik Stop #142', 'Express Mart #87'],
  created_date: '2024-01-15T10:30:00Z',
  updated_date: '2024-01-16T08:45:00Z',
  updated_by: 'system',
  pending_change_id: null,

  // Planned drops (shown as cards)
  drops: [
    {
      site: { source_name: 'Quik Stop #142', source_id: 'site_142' },
      product: { source_name: 'Regular Unleaded', goid: 'prod_reg' },
      volume: 3500,
      loads: [
        {
          terminal: { source_name: 'Houston Terminal #3' },
          supplier: { source_name: 'Gulf Coast Energy' },
          product: { source_name: 'Regular Unleaded' },
          price_type: 'rack',
        },
      ],
    },
    {
      site: { source_name: 'Express Mart #87', source_id: 'site_87' },
      product: { source_name: 'Premium Unleaded', goid: 'prod_prem' },
      volume: 2000,
      loads: [
        {
          terminal: { source_name: 'Dallas Distribution' },
          supplier: { source_name: 'Texas Fuel Co' },
          product: { source_name: 'Premium Unleaded' },
          price_type: 'contract',
        },
      ],
    },
    {
      site: { source_name: 'Corner Gas #23', source_id: 'site_23' },
      product: { source_name: 'Diesel', goid: 'prod_diesel' },
      volume: 4200,
      loads: [
        {
          terminal: { source_name: 'Houston Terminal #3' },
          supplier: { source_name: 'Gulf Coast Energy' },
          product: { source_name: 'Diesel' },
          price_type: 'rack',
        },
        {
          terminal: { source_name: 'Baytown Rack' },
          supplier: { source_name: 'Valero' },
          product: { source_name: 'Diesel' },
          price_type: 'spot',
        },
      ],
    },
  ],

  // Bills of lading (shown in grid) - 10 BOLs for testing scroll
  bols: [
    {
      bol_number: 'BOL-445521',
      terminal: { source_name: 'Houston Terminal #3' },
      sites: [
        { source_name: 'Quik Stop #142' },
        { source_name: 'Express Mart #87' },
      ],
      bol_date: '2024-01-16T06:30:00Z',
      details: [
        {
          supplier: { source_name: 'Gulf Coast Energy' },
          product: { source_name: 'Regular Unleaded' },
          contract: 'GCE-2024-001',
          price_type: 'rack',
          net_volume: 3480,
          gross_volume: 3512,
        },
        {
          supplier: { source_name: 'Texas Fuel Co' },
          product: { source_name: 'Premium Unleaded' },
          contract: 'TFC-2024-042',
          price_type: 'contract',
          net_volume: 1985,
          gross_volume: 2010,
        },
      ],
    },
    {
      bol_number: 'BOL-445522',
      terminal: { source_name: 'Baytown Rack' },
      sites: [{ source_name: 'Corner Gas #23' }],
      bol_date: '2024-01-16T07:15:00Z',
      details: [
        {
          supplier: { source_name: 'Valero' },
          product: { source_name: 'Diesel' },
          contract: 'VAL-2024-089',
          price_type: 'spot',
          net_volume: 4180,
          gross_volume: 4225,
        },
      ],
    },
    {
      bol_number: 'BOL-445523',
      terminal: { source_name: 'Dallas Distribution' },
      sites: [{ source_name: 'Fuel Depot #45' }],
      bol_date: '2024-01-16T08:00:00Z',
      details: [
        {
          supplier: { source_name: 'Shell' },
          product: { source_name: 'Regular Unleaded' },
          contract: 'SHL-2024-112',
          price_type: 'rack',
          net_volume: 2800,
          gross_volume: 2835,
        },
      ],
    },
    {
      bol_number: 'BOL-445524',
      terminal: { source_name: 'Austin Terminal' },
      sites: [{ source_name: 'Gas Plus #12' }],
      bol_date: '2024-01-16T08:45:00Z',
      details: [
        {
          supplier: { source_name: 'ExxonMobil' },
          product: { source_name: 'Premium Unleaded' },
          contract: 'EXM-2024-078',
          price_type: 'contract',
          net_volume: 3200,
          gross_volume: 3240,
        },
      ],
    },
    {
      bol_number: 'BOL-445525',
      terminal: { source_name: 'San Antonio Rack' },
      sites: [{ source_name: 'Quick Fill #33' }],
      bol_date: '2024-01-16T09:30:00Z',
      details: [
        {
          supplier: { source_name: 'Chevron' },
          product: { source_name: 'Diesel' },
          contract: 'CHV-2024-055',
          price_type: 'rack',
          net_volume: 5100,
          gross_volume: 5160,
        },
      ],
    },
    {
      bol_number: 'BOL-445526',
      terminal: { source_name: 'Houston Terminal #3' },
      sites: [{ source_name: 'Metro Gas #78' }],
      bol_date: '2024-01-16T10:15:00Z',
      details: [
        {
          supplier: { source_name: 'Gulf Coast Energy' },
          product: { source_name: 'Regular Unleaded' },
          contract: 'GCE-2024-002',
          price_type: 'spot',
          net_volume: 2650,
          gross_volume: 2680,
        },
      ],
    },
    {
      bol_number: 'BOL-445527',
      terminal: { source_name: 'Corpus Christi Terminal' },
      sites: [{ source_name: 'Coastal Fuel #19' }],
      bol_date: '2024-01-16T11:00:00Z',
      details: [
        {
          supplier: { source_name: 'Flint Hills' },
          product: { source_name: 'Premium Unleaded' },
          contract: 'FLH-2024-033',
          price_type: 'contract',
          net_volume: 1800,
          gross_volume: 1825,
        },
      ],
    },
    {
      bol_number: 'BOL-445528',
      terminal: { source_name: 'El Paso Rack' },
      sites: [{ source_name: 'Desert Stop #56' }],
      bol_date: '2024-01-16T11:45:00Z',
      details: [
        {
          supplier: { source_name: 'Marathon' },
          product: { source_name: 'Diesel' },
          contract: 'MAR-2024-067',
          price_type: 'rack',
          net_volume: 4500,
          gross_volume: 4550,
        },
      ],
    },
    {
      bol_number: 'BOL-445529',
      terminal: { source_name: 'Fort Worth Terminal' },
      sites: [{ source_name: 'City Gas #91' }],
      bol_date: '2024-01-16T12:30:00Z',
      details: [
        {
          supplier: { source_name: 'Phillips 66' },
          product: { source_name: 'Regular Unleaded' },
          contract: 'P66-2024-044',
          price_type: 'spot',
          net_volume: 3100,
          gross_volume: 3135,
        },
      ],
    },
    {
      bol_number: 'BOL-445530',
      terminal: { source_name: 'Midland Rack' },
      sites: [{ source_name: 'West Texas Fuel #8' }],
      bol_date: '2024-01-16T13:15:00Z',
      details: [
        {
          supplier: { source_name: 'HollyFrontier' },
          product: { source_name: 'Diesel' },
          contract: 'HLF-2024-021',
          price_type: 'contract',
          net_volume: 6200,
          gross_volume: 6275,
        },
      ],
    },
  ],

  // Executed drops (shown in grid) - 10 drops for testing scroll
  executed_drops: [
    {
      site: { source_name: 'Quik Stop #142' },
      mode: 'replace',
      details: [
        {
          product: { source_name: 'Regular Unleaded' },
          quantity: 3450,
          tank: { source_name: 'Tank 1' },
          pre_drop_volume: 1200,
          post_drop_volume: 4650,
          pre_drop_time: '2024-01-16T10:15:00Z',
          post_drop_time: '2024-01-16T10:45:00Z',
        },
      ],
    },
    {
      site: { source_name: 'Express Mart #87' },
      mode: 'replace',
      details: [
        {
          product: { source_name: 'Premium Unleaded' },
          quantity: 1970,
          tank: { source_name: 'Tank 2' },
          pre_drop_volume: 800,
          post_drop_volume: 2770,
          pre_drop_time: '2024-01-16T12:00:00Z',
          post_drop_time: '2024-01-16T12:25:00Z',
        },
      ],
    },
    {
      site: { source_name: 'Corner Gas #23' },
      mode: 'replace',
      details: [
        {
          product: { source_name: 'Diesel' },
          quantity: 4150,
          tank: { source_name: 'Diesel Tank A' },
          pre_drop_volume: 2500,
          post_drop_volume: 6650,
          pre_drop_time: '2024-01-16T14:30:00Z',
          post_drop_time: '2024-01-16T15:05:00Z',
        },
      ],
    },
    {
      site: { source_name: 'Fuel Depot #45' },
      mode: 'replace',
      details: [
        {
          product: { source_name: 'Regular Unleaded' },
          quantity: 2780,
          tank: { source_name: 'Tank A' },
          pre_drop_volume: 500,
          post_drop_volume: 3280,
          pre_drop_time: '2024-01-16T09:00:00Z',
          post_drop_time: '2024-01-16T09:30:00Z',
        },
      ],
    },
    {
      site: { source_name: 'Gas Plus #12' },
      mode: 'replace',
      details: [
        {
          product: { source_name: 'Premium Unleaded' },
          quantity: 3180,
          tank: { source_name: 'Premium Tank' },
          pre_drop_volume: 1100,
          post_drop_volume: 4280,
          pre_drop_time: '2024-01-16T10:00:00Z',
          post_drop_time: '2024-01-16T10:35:00Z',
        },
      ],
    },
    {
      site: { source_name: 'Quick Fill #33' },
      mode: 'replace',
      details: [
        {
          product: { source_name: 'Diesel' },
          quantity: 5050,
          tank: { source_name: 'Diesel Main' },
          pre_drop_volume: 3200,
          post_drop_volume: 8250,
          pre_drop_time: '2024-01-16T11:15:00Z',
          post_drop_time: '2024-01-16T11:55:00Z',
        },
      ],
    },
    {
      site: { source_name: 'Metro Gas #78' },
      mode: 'replace',
      details: [
        {
          product: { source_name: 'Regular Unleaded' },
          quantity: 2620,
          tank: { source_name: 'Tank 1' },
          pre_drop_volume: 900,
          post_drop_volume: 3520,
          pre_drop_time: '2024-01-16T12:45:00Z',
          post_drop_time: '2024-01-16T13:10:00Z',
        },
      ],
    },
    {
      site: { source_name: 'Coastal Fuel #19' },
      mode: 'replace',
      details: [
        {
          product: { source_name: 'Premium Unleaded' },
          quantity: 1780,
          tank: { source_name: 'Tank 2' },
          pre_drop_volume: 600,
          post_drop_volume: 2380,
          pre_drop_time: '2024-01-16T13:30:00Z',
          post_drop_time: '2024-01-16T13:55:00Z',
        },
      ],
    },
    {
      site: { source_name: 'Desert Stop #56' },
      mode: 'replace',
      details: [
        {
          product: { source_name: 'Diesel' },
          quantity: 4480,
          tank: { source_name: 'Diesel Tank B' },
          pre_drop_volume: 1800,
          post_drop_volume: 6280,
          pre_drop_time: '2024-01-16T14:00:00Z',
          post_drop_time: '2024-01-16T14:40:00Z',
        },
      ],
    },
    {
      site: { source_name: 'City Gas #91' },
      mode: 'replace',
      details: [
        {
          product: { source_name: 'Regular Unleaded' },
          quantity: 3080,
          tank: { source_name: 'Main Tank' },
          pre_drop_volume: 1500,
          post_drop_volume: 4580,
          pre_drop_time: '2024-01-16T15:15:00Z',
          post_drop_time: '2024-01-16T15:45:00Z',
        },
      ],
    },
  ],
}

export const MOCK_ORDER_RESPONSE: OrderDetailResponse = {
  order: {
    internal_order: MOCK_ORDER,
    partner_order: {
      ...MOCK_ORDER,
      internal_order_number: 'PTN-78542',
      partner_order_number: 'PTN-78542',
    },
    gravitate_order: {
      ...MOCK_ORDER,
      internal_order_number: 'GRV-2024-001234',
    },
    flags: [],
  },
}
